/**
 * D13 — Donation Tracking.
 *
 * Renders the steps the server sends. Exceptional outcomes — payment failed,
 * cancelled, refunded — get an explicit banner rather than a stalled timeline,
 * which is what the artwork would have shown since it designed no such state.
 */

import { StyleSheet, View } from 'react-native';

import { formatDate, formatMoney, formatPeople, hasReachedDistribution } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { ErrorState } from '../../components/feedback/States';
import { Glyph } from '../../components/common/Icon';
import { OrderStatusBadge } from '../../components/common/Badge';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { TrackingTimeline } from '../../components/donation/TrackingTimeline';
import { useTracking } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function TrackingScreen({ navigation, route }: DonorScreenProps<'Tracking'>) {
  const t = useT();
  const pick = useLocalized();
  const { donationId } = route.params;
  const query = useTracking(donationId);

  if (query.isError) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-tracking">
        <ErrorState
          title={t('Tracking unavailable')}
          message={t("We could not load this donation's progress.")}
          onRetry={() => void query.refetch()}
        />
      </ScreenShell>
    );
  }

  const tracking = query.data;
  const donation = tracking?.donation;
  const proofReady = donation ? hasReachedDistribution(donation.orderStatus) : false;

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      onRefresh={() => void query.refetch()}
      refreshing={query.isRefetching}
      testID="screen-tracking"
      pinnedFooter={
        proofReady ? (
          <Button
            label={t('View Distribution Proof')}
            onPress={() => navigation.navigate('DistributionProof', { donationId })}
            testID="cta-view-proof"
          />
        ) : undefined
      }
    >
      {query.isLoading || !donation ? (
        <View style={styles.loading}>
          <Skeleton height={32} width="60%" />
          <Skeleton height={20} width="40%" />
          <Skeleton height={280} />
        </View>
      ) : (
        <>
          <View style={styles.headerRow}>
            <Text variant="screenTitle" testID="tracking-reference">
              #{donation.publicReference}
            </Text>
            <OrderStatusBadge status={donation.orderStatus} testID="tracking-status" />
          </View>

          <Text variant="cardTitle" style={styles.mealName}>
            {pick(donation.meal.name, donation.meal.nameUr)}
          </Text>

          <View style={styles.metaRow}>
            <Meta icon="people" value={formatPeople(donation.peopleCount)} />
            <Meta icon="price" value={formatMoney(donation.totalAmount)} />
            <Meta icon="calendar" value={formatDate(donation.distributionDate)} />
          </View>

          {tracking?.exception ? (
            <View style={styles.exception} accessibilityLiveRegion="polite" testID="tracking-exception">
              <Text variant="bodyStrong" tone="danger">
                {t(tracking.exception.label)}
              </Text>
              <Text variant="caption" tone="secondary">
                {tracking.exception.message}
              </Text>
            </View>
          ) : null}

          <Card title={t('Donation Progress')} style={styles.card}>
            <TrackingTimeline steps={tracking?.steps ?? []} />
          </Card>

          <View style={styles.notice}>
            <Glyph name="privacy" size={18} />
            <Text variant="caption" tone="secondary" style={styles.noticeText}>{t("We'll notify you at every step until your donation is completed.")}</Text>
          </View>

          <Button
            label={t('View Receipt')}
            variant="secondary"
            onPress={() => navigation.navigate('Receipt', { donationId })}
            style={styles.receiptButton}
            testID="cta-receipt"
          />
        </>
      )}
    </ScreenShell>
  );
}

function Meta({ icon, value }: { icon: string; value: string }) {
  return (
    <View style={styles.meta}>
      <Glyph name={icon} size={14} />
      <Text variant="caption" tone="secondary">
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  loading: {
    gap: space.md,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.md,
  },
  mealName: {
    marginTop: space.xs,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.lg,
    marginTop: space.md,
    marginBottom: space.lg,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  exception: {
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.action.dangerSubtle,
    gap: space.xxs,
    marginBottom: space.lg,
  },
  card: {
    marginTop: space.sm,
  },
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginTop: space.lg,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.brandSubtle,
  },
  noticeText: {
    flex: 1,
  },
  receiptButton: {
    marginTop: space.lg,
  },
});
